// src/Components/SEO/EnhancedSEO.jsx
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

const EnhancedSEO = ({ 
  title = 'Revfer - Professional Networking Platform',
  description = 'Connect with professionals, grow your network, and advance your career with Revfer.',
  keywords = 'professional networking, career growth, business connections',
  image = '/og-image.jpg',
  type = 'website',
  
  // Enhanced props for About page
  organizationData = null,
  author = 'Revfer Team',
  datePublished = null,
  dateModified = null,
  breadcrumbs = null
}) => {
  const location = useLocation();
  
  const canonicalUrl = `https://www.revfer.com${location.pathname}`;
  const imageUrl = image.startsWith('http') ? image : `https://www.revfer.com${image}`;

  // Default organization structured data
  const defaultOrgData = {
    "@context": "https://schema.org",
    "@type": "TechnologyCompany",
    "name": "Revfer",
    "description": "AI platform democratizing enterprise technology for small businesses",
    "url": "https://www.revfer.com",
    "logo": "https://www.revfer.com/logo.png",
    "foundingDate": "2022",
    "founders": [
      {
        "@type": "Person",
        "name": "Alex Johnson",
        "jobTitle": "CEO & Co-Founder"
      },
      {
        "@type": "Person", 
        "name": "Sarah Chen",
        "jobTitle": "CTO & Co-Founder"
      }
    ],
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "59 Grover Rd",
      "addressLocality": "Ashland",
      "addressRegion": "MA",
      "postalCode": "01721",
      "addressCountry": "US"
    },
    "contactPoint": [
      {
        "@type": "ContactPoint",
        "telephone": "+1-XXX-XXX-XXXX",
        "contactType": "customer service",
        "email": "hello@revfer.com"
      },
      {
        "@type": "ContactPoint",
        "contactType": "sales",
        "email": "partnerships@revfer.com"
      }
    ],
    "sameAs": [
      "https://twitter.com/revfer",
      "https://linkedin.com/company/revfer"
    ],
    "industry": "Artificial Intelligence",
    "numberOfEmployees": "11-50",
    "keywords": "AI for small business, machine learning, business intelligence, professional networking",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web Browser",
    "offers": {
      "@type": "Offer",
      "description": "AI-powered business intelligence and networking platform",
      "category": "SaaS"
    }
  };

  // Generate breadcrumb structured data
  const breadcrumbSchema = breadcrumbs ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": crumb.name,
      "item": `https://www.revfer.com${crumb.url}`
    }))
  } : null;

  // Article schema for content pages
  const articleSchema = (type === 'article' && datePublished) ? {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": title,
    "description": description,
    "image": imageUrl,
    "author": {
      "@type": "Organization",
      "name": author
    },
    "publisher": {
      "@type": "Organization",
      "name": "Revfer",
      "logo": {
        "@type": "ImageObject",
        "url": "https://www.revfer.com/logo.png"
      }
    },
    "datePublished": datePublished,
    "dateModified": dateModified || datePublished,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": canonicalUrl
    }
  } : null;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content="Revfer" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:site" content="@revfer" />
      
      {/* Additional SEO */}
      <meta name="robots" content="index, follow" />
      <meta name="author" content={author} />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      
      {/* Language and Locale */}
      <meta httpEquiv="Content-Language" content="en" />
      <meta property="og:locale" content="en_US" />
      
      {/* Organization Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(organizationData || defaultOrgData)}
      </script>
      
      {/* Breadcrumb Structured Data */}
      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}
      
      {/* Article Structured Data */}
      {articleSchema && (
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
      )}
      
      {/* Additional Meta for About Page */}
      {location.pathname === '/legal/about' && (
        <>
          <meta name="company" content="Revfer Inc." />
          <meta name="founded" content="2022" />
          <meta name="location" content="Ashland, Massachusetts" />
          <meta name="industry" content="Artificial Intelligence, SaaS, Professional Networking" />
          <meta name="business-model" content="B2B SaaS Platform" />
          <meta name="target-audience" content="Small businesses, Local businesses, Professional networks" />
        </>
      )}
    </Helmet>
  );
};

export default EnhancedSEO;